/**
 * 開発者アカウントの閉鎖（pages/AccountPage.tsx）の判断を React から切り離したもの（node --test で確かめる）。
 *
 * API の正: apps/server/src/modules/platform/accountClose.ts（DELETE /v1/developers/me {password}・kds_ のみ・1 時間 5 回）。
 *   - 401 reauthentication_failed = パスワード違い（セッションは生きている = ログアウトさせない。api.ts）
 *   - 409 projects_remain = 動いているゲームが残っている（サーバーは黙って消さない → 画面で一覧と消し方を出す）
 *   - 429 rate_limited = 1 時間 5 回を超えた
 *   - 200 = 閉鎖済み。前払いの返金の依頼はサーバーが自動で出す（本人の選択は無い）。全セッションが失効 = この端末もログアウト
 *
 * WHY 打ち込みの確認か（ボタン 1 つにしないか）: 閉鎖は再開の道が無い（管理判断）。「確認」を押すだけだと
 * 流れで押せてしまうので、メールアドレスか CLOSE を打たせる（GitHub のリポジトリ削除などと同じ流儀）。
 * パスワードはサーバーの再認証（盗まれたセッションだけで閉じさせない）で、打ち込みの確認とは別の目的。
 */

/** 打ち込みの確認で受け付ける固定の語（どの言語の画面でも同じ = 翻訳しない。CLI も同じ語） */
export const CLOSE_WORD = 'CLOSE';

/**
 * 打ち込みの確認が合っているか。メールアドレス（大文字小文字・前後の空白は無視 = メールは大文字小文字を区別しない運用）か、
 * CLOSE（大文字だけ = 「わざわざ打った」ことを確かめる語なので close は受けない）
 */
export function closeConfirmOk(typed: string, email: string): boolean {
  const v = typed.trim();
  if (!v) return false;
  if (v === CLOSE_WORD) return true;
  const e = email.trim().toLowerCase();
  return e.length > 0 && v.toLowerCase() === e;
}

/**
 * 送信ボタンを押せるか（確認の語 + パスワード。ゲームが残っているなら押させない = 409 が分かっている要求を送らない）。
 * passwordless（2026-09-27 creator/dev-oauth-ui）: Google / Apple / GitHub だけの人（developer.hasPassword === false）は
 * パスワードの欄が無い。送信の後に再認証のダイアログ（components/Reauth.tsx）で「Google で確認」をするので、パスワードは要らない
 */
export function canSubmitClose(o: { typed: string; email: string; password: string; projectsLeft: number; busy?: boolean; passwordless?: boolean }): boolean {
  return !o.busy && o.projectsLeft === 0 && (o.passwordless || o.password.length > 0) && closeConfirmOk(o.typed, o.email);
}

export type CloseFailure = 'projects_remain' | 'wrong_password' | 'rate_limited' | 'other';

/** 閉鎖の要求の失敗を画面の出し分けに変える（code はサーバーの error.code） */
export function closeFailureKind(err: { status: number; code: string }): CloseFailure {
  if (err.status === 409 && err.code === 'projects_remain') return 'projects_remain';
  if (err.status === 401 && err.code === 'reauthentication_failed') return 'wrong_password';
  if (err.status === 429 || err.code === 'rate_limited') return 'rate_limited';
  return 'other';
}

/** ゲームの削除の確認（slug を打たせる。featureToggle.ts の strongConfirmOk と同じ = 完全一致） */
export function deleteGameConfirmOk(typed: string, slug: string): boolean {
  return typed.trim() === slug;
}
